import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, map, Observable, throwError } from 'rxjs';
import { ApiService, QueryParams } from './api.service';
import {
  AvailableProductsParams,
  CompareResponse,
  Paginated,
  Product,
  ProductNotFoundError,
} from '../models/product.model';

@Injectable({ providedIn: 'root' })
export class ProductService {
  private readonly api = inject(ApiService);

  getProducts(params?: AvailableProductsParams): Observable<Paginated<Product>> {
    const query: QueryParams = {};
    if (params?.search !== undefined) query['search'] = params.search;
    if (params?.skipZeroStock !== undefined) query['skipZeroStock'] = params.skipZeroStock;
    if (params?.per_page !== undefined) query['per_page'] = params.per_page;
    if (params?.page !== undefined) query['page'] = params.page;
    if (params?.sort !== undefined) query['sort'] = params.sort;
    if (params?.sort_by !== undefined) query['sort_by'] = params.sort_by;
    if (params?.order !== undefined) query['order'] = params.order;
    if (params?.skip_paging !== undefined) query['skip_paging'] = params.skip_paging;
    if (params?.include_prepurchase !== undefined) query['include_prepurchase'] = params.include_prepurchase;
    if (params?.on_discount !== undefined) query['on_discount'] = params.on_discount;

    return this.api.get<Paginated<Product>>('/avaliable-products', query);
  }

  getProduct(id: number | string): Observable<Product> {
    return this.api
      .get<{ data: Product }>(`/show-web-product/${id}`)
      .pipe(map((res) => res.data));
  }

  /**
   * Detalle por slug SEO. Backend: GET /show-web-product-by-slug/{slug}.
   * Devuelve el mismo shape que `/show-web-product/{id}` (scoped route-model
   * binding en Laravel). Distingue dos tipos de 404:
   * - Slug inexistente: ModelNotFoundException (body vacío o genérico).
   * - Producto con enabled=false: 404 con body `{ error: "El producto no está disponible" }`.
   */
  getProductBySlug(slug: string): Observable<Product> {
    return this.api
      .get<{ data: Product }>(`/show-web-product-by-slug/${slug}`)
      .pipe(
        map((res) => res.data),
        catchError((err: unknown) => {
          if (err instanceof HttpErrorResponse && err.status === 404) {
            const body = (err.error?.error ?? '').toString();
            const reason = body.includes('no está disponible') ? 'disabled' : 'not_found';
            return throwError(() => new ProductNotFoundError(slug, reason, body || undefined));
          }
          return throwError(() => err);
        }),
      );
  }

  getRelated(
    id: number | string,
  ): Observable<{ related: Product[]; frequentlyBoughtTogether: Product[] }> {
    return this.getProduct(id).pipe(
      map((product) => ({
        related: product.related_products ?? [],
        frequentlyBoughtTogether: product.frequently_bought_together ?? [],
      })),
    );
  }

  compareProducts(ids: number[]): Observable<CompareResponse> {
    return this.api.post<CompareResponse>('/products/compare', { ids });
  }
}
